import { Link, Typography } from '@mui/material';
import React, { memo, useMemo } from 'react';

import { Pagination } from '~/components/Pagination';
import { SearchPitchbookCompanyResponse } from '~/services/rtkQuery/company/types';
import { formatUrl } from '~/utils/formats';
import { getCurrentPageInfo, getTotalPagesCount } from '~/utils/pagination';

import styles from './styles.module.scss';

interface Props {
  companies: SearchPitchbookCompanyResponse['result']['data'];
  totalCount: number;
  perPageLimit: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onCompanyClick: (
    event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    company: SearchPitchbookCompanyResponse['result']['data'][number],
  ) => void;
}

const Component: React.FC<Props> = ({
  companies,
  totalCount,
  perPageLimit,
  currentPage,
  onPageChange,
  onCompanyClick,
}) => {
  const totalPages = useMemo(() => {
    return getTotalPagesCount({
      totalItemsCount: totalCount,
      fetchLimit: perPageLimit,
    });
  }, [perPageLimit, totalCount]);

  return (
    <div>
      <table className={styles.container__table}>
        <thead>
          <tr>
            <th>
              <Typography color="text.primary" fontWeight="300" variant="body1">
                Company Name
              </Typography>
            </th>
            <th>
              <Typography color="text.primary" fontWeight="300" variant="body1">
                Website
              </Typography>
            </th>
            <th>
              <Typography color="text.primary" fontWeight="300" variant="body1">
                Pitchbook Link
              </Typography>
            </th>
            <th>
              <Typography color="text.primary" fontWeight="300" variant="body1">
                Source
              </Typography>
            </th>
          </tr>
        </thead>
        <tbody>
          {companies.map(company => (
            <tr key={company.pbId} onClick={event => onCompanyClick(event, company)}>
              <td>
                <Typography color="text.primary" fontWeight="500" variant="body1">
                  {company.companyname}
                </Typography>
              </td>
              <td>
                <Link
                  color="rgb(var(--main-2-color-rgb))"
                  fontWeight="500"
                  href={company.website ? formatUrl(company.website) : undefined}
                  lineHeight="20px"
                  target="_blank"
                  underline="none"
                  variant="body1"
                >
                  {company.website}
                </Link>
              </td>
              <td>
                <Link
                  color="rgb(var(--main-2-color-rgb))"
                  fontWeight="500"
                  href={formatUrl(company.pitchBookProfileLink)}
                  target="_blank"
                  variant="body1"
                >
                  {company.pitchBookProfileLink}
                </Link>
              </td>
              <td>
                <div className={styles['container__source-badge']}>
                  <Typography
                    color={
                      company.source === 'internal' ? 'var(--main-2-color)' : 'var(--main-1-color)'
                    }
                    variant="body1"
                  >
                    {company.source === 'internal' ? 'Internal' : 'Pitchbook'}
                  </Typography>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={styles.container__footer}>
        <Typography color="var(--gray-2-color)" variant="subtitle2">
          {getCurrentPageInfo({
            currentPage,
            fetchLimit: perPageLimit,
            currentPageItemsCount: companies.length,
            totalItemsCount: totalCount,
          })}
        </Typography>
        <Pagination currentPage={currentPage} totalPages={totalPages} onChange={onPageChange} />
      </div>
    </div>
  );
};

export const Companies = memo(Component);
